import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './PhoneNumberModal.css';

// Common country codes
const COUNTRY_CODES = [
  { code: '+91', name: 'India (IN)', flag: '🇮🇳' },
  { code: '+1', name: 'USA/Canada (US/CA)', flag: '🇺🇸' },
  { code: '+44', name: 'United Kingdom (GB)', flag: '🇬🇧' },
  { code: '+971', name: 'UAE (AE)', flag: '🇦🇪' },
  { code: '+65', name: 'Singapore (SG)', flag: '🇸🇬' },
  { code: '+61', name: 'Australia (AU)', flag: '🇦🇺' },
  { code: '+33', name: 'France (FR)', flag: '🇫🇷' },
  { code: '+49', name: 'Germany (DE)', flag: '🇩🇪' },
  { code: '+81', name: 'Japan (JP)', flag: '🇯🇵' },
  { code: '+86', name: 'China (CN)', flag: '🇨🇳' },
  { code: '+92', name: 'Pakistan (PK)', flag: '🇵🇰' },
  { code: '+880', name: 'Bangladesh (BD)', flag: '🇧🇩' },
  { code: '+94', name: 'Sri Lanka (LK)', flag: '🇱🇰' },
  { code: '+977', name: 'Nepal (NP)', flag: '🇳🇵' },
  { code: '+234', name: 'Nigeria (NG)', flag: '🇳🇬' },
  { code: '+27', name: 'South Africa (ZA)', flag: '🇿🇦' },
  { code: '+55', name: 'Brazil (BR)', flag: '🇧🇷' },
  { code: '+52', name: 'Mexico (MX)', flag: '🇲🇽' },
  { code: '+34', name: 'Spain (ES)', flag: '🇪🇸' },
  { code: '+39', name: 'Italy (IT)', flag: '🇮🇹' }
];

const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

const PhoneNumberModal = ({ isOpen, onClose, onSuccess, onSkip }) => {
  const { user, setUser } = useAuth();
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [bonusCredits, setBonusCredits] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiCanvasRef = useRef(null);
  
  // OTP state
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const otpTimerRef = useRef(null);

  // Set default to India if user is from India or no preference
  React.useEffect(() => {
    if (isOpen && !selectedCountryCode) {
      setSelectedCountryCode('+91');
    }
  }, [isOpen, selectedCountryCode]);

  // Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');

    // Validate phone number
    const cleanedPhone = phoneNumber.replace(/\s+/g, '');
    if (!cleanedPhone || cleanedPhone.length < 6 || cleanedPhone.length > 15) {
      setError('Please enter a valid phone number (6-15 digits)');
      return;
    }

    if (!/^\d+$/.test(cleanedPhone)) {
      setError('Phone number must contain only digits');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const requestBody = {
        countryCode: selectedCountryCode,
        phoneNumber: cleanedPhone
      };
      const requestUrl = `${BACKEND_URL}/api/user/phone/request-otp`;
      
      const response = await axios.post(
        requestUrl,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setOtpRequested(true);
        setStep('otp');
        setOtpTimer(600); // 10 minutes in seconds
        setError('');
        
        // Show warning if OTP was generated but not sent via SMS
        if (response.data.warning) {
          // Display warning but allow user to continue (OTP is still valid)
          let warningMessage = response.data.warning;
          // Add development OTP note if provided
          if (response.data.note) {
            warningMessage += ' ' + response.data.note;
          }
          // Add OTP in development mode if provided
          if (response.data.otp && process.env.NODE_ENV === 'development') {
            warningMessage += ` Development: OTP is ${response.data.otp}. In production, OTP would be sent via SMS.`;
          }
          setError(warningMessage);
        }
        
        // Start countdown timer
        if (otpTimerRef.current) {
          clearInterval(otpTimerRef.current);
        }
        otpTimerRef.current = setInterval(() => {
          setOtpTimer((prev) => {
            if (prev <= 1) {
              clearInterval(otpTimerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.data.error || 'Failed to generate OTP');
      }
    } catch (err) {
      // Comprehensive error logging
      console.error('❌ Error Requesting OTP:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
        phoneNumber: `${selectedCountryCode} ${phoneNumber}`,
        timestamp: new Date().toISOString(),
        stack: process.env.NODE_ENV === 'development' ? err.stack : undefined
      });
      
      // Log detailed error information
      if (err.response) {
        console.error('📡 API Response Error:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers
        });
      } else if (err.request) {
        console.error('🌐 Network Error:', {
          message: 'No response received from server',
          request: err.request
        });
      } else {
        console.error('🔧 Request Setup Error:', err.message);
      }
      
      // Display user-friendly error message
      const errorMessage = err.response?.data?.error || 
        (err.response?.status >= 500 
          ? 'Service temporarily unavailable. Please try again in a moment.'
          : err.response?.status === 400
          ? 'Please check your phone number and try again.'
          : 'Unable to generate OTP. Please try again.');
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Verify OTP and complete phone number update
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    if (!/^\d{6}$/.test(otpCode)) {
      setError('OTP must contain only digits');
      return;
    }
    
    // Clean phone number for API request
    const cleanedPhone = phoneNumber.replace(/\s+/g, '');

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const requestBody = {
        countryCode: selectedCountryCode,
        phoneNumber: cleanedPhone,
        otpCode: otpCode
      };
      const requestUrl = `${BACKEND_URL}/api/user/phone/verify-otp`;
      
      const response = await axios.post(
        requestUrl,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Clear OTP timer
        if (otpTimerRef.current) {
          clearInterval(otpTimerRef.current);
        }

        // Update user in context
        setUser(response.data.user);
        
        // Show success message with bonus credits
        if (response.data.bonusCredits) {
          setBonusCredits(response.data.bonusCredits);
          setShowSuccess(true);
          setShowConfetti(true);
          
          // Stop confetti after 5 seconds
          setTimeout(() => {
            setShowConfetti(false);
          }, 5000);
          
          // Auto close after 3 seconds to let user see the success message
          setTimeout(() => {
            setShowSuccess(false);
            onSuccess && onSuccess(response.data.user, response.data.bonusCredits);
            onClose();
          }, 3000);
        } else {
          onSuccess && onSuccess(response.data.user);
          onClose();
        }
      } else {
        // Use the error message from server (already user-friendly)
        setError(response.data.error || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      // Comprehensive error logging for OTP verification
      console.error('❌ Error Verifying OTP:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
        phoneNumber: `${selectedCountryCode} ${cleanedPhone}`,
        otpCode: otpCode,
        timestamp: new Date().toISOString(),
        stack: process.env.NODE_ENV === 'development' ? err.stack : undefined
      });
      
      // Log detailed error information
      if (err.response) {
        console.error('📡 API Response Error (OTP Verification):', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers
        });
      } else if (err.request) {
        console.error('🌐 Network Error (OTP Verification):', {
          message: 'No response received from server',
          request: err.request
        });
      } else {
        console.error('🔧 Request Setup Error (OTP Verification):', err.message);
      }
      
      // Display user-friendly error message
      const errorMessage = err.response?.data?.error || 
        (err.response?.status >= 500 
          ? 'Service temporarily unavailable. Please try again in a moment.'
          : err.response?.status === 400
          ? 'Please check the OTP code and try again.'
          : 'Unable to verify OTP. Please try again.');
      
      setError(errorMessage);
      // Clear OTP input on error
      setOtpCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all states when modal closes
      setStep('phone');
      setOtpCode('');
      setOtpRequested(false);
      setOtpTimer(0);
      setError('');
      setShowSuccess(false);
      setBonusCredits(null);
      setShowConfetti(false);
      if (otpTimerRef.current) {
        clearInterval(otpTimerRef.current);
      }
    }
  }, [isOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (otpTimerRef.current) {
        clearInterval(otpTimerRef.current);
      }
    };
  }, []);

  // Confetti animation effect
  useEffect(() => {
    if (showConfetti && confettiCanvasRef.current) {
      const canvas = confettiCanvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const confetti = [];
      // Adjust confetti count based on screen size (performance optimization)
      const confettiCount = window.innerWidth < 768 ? 100 : 150;
      const colors = ['#6A28D7', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#F5F3FF'];
      const gravity = 0.5;
      const terminalVelocity = 5;
      const drag = 0.075;

      // Create confetti particles
      for (let i = 0; i < confettiCount; i++) {
        confetti.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
          color: colors[Math.floor(Math.random() * colors.length)],
          dimensions: {
            x: Math.random() * 10 + 5,
            y: Math.random() * 10 + 5
          },
          rotation: Math.random() * 2 * Math.PI,
          rotationSpeed: Math.random() * 2,
          velocity: {
            x: Math.random() * 20 - 10,
            y: Math.random() * 5 + 5
          },
          opacity: Math.random() * 0.8 + 0.2
        });
      }

      let animationId;
      function animate() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        confetti.forEach((particle, index) => {
          ctx.save();
          ctx.translate(particle.x, particle.y);
          ctx.rotate(particle.rotation);
          ctx.globalAlpha = particle.opacity;
          ctx.fillStyle = particle.color;
          ctx.fillRect(-particle.dimensions.x / 2, -particle.dimensions.y / 2, particle.dimensions.x, particle.dimensions.y);
          ctx.restore();

          // Update physics
          particle.rotation += particle.rotationSpeed;
          particle.velocity.y += gravity;
          particle.velocity.y = Math.min(particle.velocity.y, terminalVelocity);
          particle.velocity.x *= (1 - drag);
          particle.x += particle.velocity.x;
          particle.y += particle.velocity.y;

          // Reset if off screen
          if (particle.y > canvas.height) {
            particle.y = -10;
            particle.x = Math.random() * canvas.width;
          }
        });

        if (showConfetti) {
          animationId = requestAnimationFrame(animate);
        }
      }

      animate();

      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [showConfetti]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (confettiCanvasRef.current) {
        confettiCanvasRef.current.width = window.innerWidth;
        confettiCanvasRef.current.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {showConfetti && (
        <canvas
          ref={confettiCanvasRef}
          className="phone-confetti-canvas"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 10001
          }}
        />
      )}
      <div className="phone-modal-overlay" onClick={(e) => {
        // Allow closing by clicking outside (skip option available)
        if (onSkip && !showSuccess) {
          onSkip();
        }
      }}>
        <div className="phone-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="phone-modal-header">
          <h2>Add Phone Number</h2>
        </div>

        <div className="phone-modal-body">
          {step === 'phone' && (
            <>
              <div className="phone-bonus-banner">
                <span className="phone-bonus-icon">🎁</span>
                <span className="phone-bonus-text">Get <strong>10 FREE Credits</strong> when you verify your phone number!</span>
              </div>
              <p className="phone-modal-description">
                Verify your phone number with OTP to secure your account and receive bonus credits.
              </p>

              <form onSubmit={handleRequestOTP} className="phone-modal-form">
            <div className="phone-input-group">
              <label htmlFor="country-code">Country Code</label>
              <select
                id="country-code"
                value={selectedCountryCode}
                onChange={(e) => setSelectedCountryCode(e.target.value)}
                className="phone-country-select"
                required
              >
                {COUNTRY_CODES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code} - {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="phone-input-group">
              <label htmlFor="phone-number">Phone Number</label>
              <input
                id="phone-number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setPhoneNumber(value);
                  setError('');
                }}
                placeholder="Enter your phone number"
                className="phone-number-input"
                required
                autoFocus
                maxLength={15}
              />
              <small className="phone-input-hint">Enter digits only (6-15 digits)</small>
            </div>

            {error && (
              <div className={`phone-modal-error ${error.includes('warning') || error.toLowerCase().includes('unable to send') || error.toLowerCase().includes('development') ? 'warning' : ''}`}>
                <strong>⚠️</strong> {error}
              </div>
            )}

            {!showSuccess && (
              <div className="phone-modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    if (onSkip) {
                      onSkip();
                      onClose();
                    }
                  }}
                  className="phone-modal-skip"
                  disabled={isSubmitting}
                >
                  Skip for Now
                </button>
                <button
                  type="submit"
                  className="phone-modal-submit"
                  disabled={isSubmitting || !phoneNumber.trim()}
                >
                  {isSubmitting ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </div>
            )}
          </form>
            </>
          )}

          {step === 'otp' && (
            <>
              <div className="phone-bonus-banner">
                <span className="phone-bonus-icon">📱</span>
                <span className="phone-bonus-text">Enter the <strong>6-digit OTP</strong> sent to your phone</span>
              </div>
              <p className="phone-modal-description">
                OTP sent to <strong>{selectedCountryCode} {phoneNumber}</strong>
              </p>

              <form onSubmit={handleVerifyOTP} className="phone-modal-form">
                <div className="phone-input-group">
                  <label htmlFor="otp-code">OTP Code</label>
                  <input
                    id="otp-code"
                    type="text"
                    value={otpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtpCode(value);
                      setError('');
                    }}
                    placeholder="Enter 6-digit OTP"
                    className="phone-number-input"
                    required
                    autoFocus
                    maxLength={6}
                  />
                  <small className="phone-input-hint">
                    {otpTimer > 0 ? (
                      <>OTP expires in <strong>{Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}</strong></>
                    ) : (
                      <>OTP expired. Please request a new one.</>
                    )}
                  </small>
                </div>

                <div className="phone-modal-resend-section">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpCode('');
                      setError('');
                      setStep('phone');
                      setOtpRequested(false);
                      if (otpTimerRef.current) {
                        clearInterval(otpTimerRef.current);
                      }
                      setOtpTimer(0);
                    }}
                    className="phone-resend-link"
                    disabled={isSubmitting}
                  >
                    ← Change Phone Number
                  </button>
                  <button
                    type="button"
                    onClick={handleRequestOTP}
                    className="phone-resend-link"
                    disabled={isSubmitting || otpTimer > 540} // Can resend after 1 minute
                  >
                    {otpTimer > 540 ? `Resend OTP (${Math.floor((otpTimer - 540) / 60)}:${((otpTimer - 540) % 60).toString().padStart(2, '0')})` : 'Resend OTP'}
                  </button>
                </div>

                {error && (
                  <div className={`phone-modal-error ${error.toLowerCase().includes('invalid') || error.toLowerCase().includes('expired') || error.toLowerCase().includes('verification') ? '' : 'warning'}`}>
                    <strong>⚠️</strong> {error}
                  </div>
                )}

                {showSuccess && bonusCredits && (
              <div className="phone-modal-success">
                <span className="phone-success-icon">🎉</span>
                <p className="phone-success-title">Congratulations!</p>
                <p>Phone number added successfully!</p>
                <p className="phone-bonus-announcement">
                  <strong>+{bonusCredits} FREE Credits</strong> have been added to your account!
                </p>
                <div className="phone-celebration-emojis">
                  <span className="celebration-emoji">🎁</span>
                  <span className="celebration-emoji">✨</span>
                  <span className="celebration-emoji">🎊</span>
                  <span className="celebration-emoji">🎈</span>
                </div>
                </div>
                )}

                {!showSuccess && (
                  <div className="phone-modal-actions">
                    <button
                      type="submit"
                      className="phone-modal-submit phone-modal-submit-full"
                      disabled={isSubmitting || !otpCode || otpCode.length !== 6 || otpTimer === 0}
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify OTP & Get 10 Credits'}
                    </button>
                  </div>
                )}
              </form>
            </>
          )}

        </div>
      </div>
    </div>
    </>
  );
};

export default PhoneNumberModal;

